import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';
import { Group as LayerGroup } from 'ol/layer';

import { Map as OwMap, getOwMap } from '../../general/location/Map';
import DeviceStateMapMarker from './DeviceStateMapMarker';
import { actions as zonesActions } from '../../../redux/api/zones';
import { setProps } from '../../../misc/ol';
import { readMultiPolygon } from '../../../misc/wkt';
import { makeStyle, StyleType } from '../zones/zoneStyle';
import { URL_DELIMITER } from '../../../misc/url';
import useLocalStorage from '../../../misc/useLocalStorage';
import { className } from '../../../lib/className';
import { MarkerOrderManager } from '../../general/location/MarkerOrderManager';
import { MarkerHoverManager } from '../../general/location/MarkerHoverManager';
import MarkerOrderOption from './controls/MarkerOrderOption';
import { useGeoJsonLayers } from 'hooks/map/useGeoJsonLayers';

import './overview.scss';
import {
	guardedIssuesSelector,
	digestSelector,
	mruSelector,
	resolutionTypesSelector,
	runwaysSelector,
} from '../../../redux/selectors/registry';
import { ResolutionKind } from '../../../../constants/issues';
import { actions } from '../../../redux/api/veta/runwayGuardDigest';
import { actions as runwayActions } from '../../../redux/api/veta/runways';
import { BadgeTypes, DeviceIssueMnemonics } from '../widgets/RunwayInfo';
import { categoriesSelector } from '../../../redux/selectors/categories';
import { hotIssuesSelector, issuesTypesStateSelector } from '../../../redux/selectors/issues';
import { Compass } from './controls/compass';
import { MAP_DEFAULT_VIEW } from 'constants/map';

const DEFAULT_MAP_NAME = 'overview';

/**
 * @param {Object} props
 * @param {string} [props.name]
 * @param {React.Component} [props.control]
 * @param {React.Ref} [props.customRef]
 */

function OverviewMap(props) {
	const map = useRef(null);
	const [inited, setInited] = useState(false);
	const layerGroup = useRef(null);
	const mapName = props.name || DEFAULT_MAP_NAME;
	const [displayZones, setDisplayZones] = useLocalStorage(mapName + '_display_zones');
	const [orderOption, setOrderOption] = useLocalStorage(mapName + '_marker_order_option');
	const [defaultView, setDefaultView] = useLocalStorage(`${mapName}_default_view`);
	const uris = props.match.params.uris ? props.match.params.uris.split(URL_DELIMITER) : [];
	const { currentCategoriesIds, hiddenCategoriesIds } = props.categoriesVisibility;

	const layersMethods = useGeoJsonLayers(mapName, map.current);

	const focusMarkers = () => {
		if (inited && !!uris.length) {
			getOwMap(mapName).focusMarkers(uris);
		}
	};

	const focusDefaultView = () => {
		if (map.current) {
			const setView = (options) => map.current.getOlMap().getView().animate(options);

			if (defaultView) {
				setView({ center: defaultView.center, zoom: defaultView.zoom, duration: 0 });
			} else {
				setView({ ...MAP_DEFAULT_VIEW, duration: 0 });
				setDefaultView(MAP_DEFAULT_VIEW);
			}
		}
	};

	const saveMapView = () => {
		if (inited && map.current) {
			const zoom = map.current.getOlMap().getView().getZoom();
			const center = map.current.getOlMap().getView().getCenter();

			setDefaultView({ zoom, center });
		}
	};

	useEffect(() => {
		if (props.customRef) props.customRef.current = map.current;
		if (props.zones.map == null) {
			props.dispatch(zonesActions.load.request());
		}
		map.current.updateSize();
		const timeOut = setTimeout(() => {
			setInited(true);
		}, 1000);
		return () => clearTimeout(timeOut);
	}, []);

	useEffect(() => {
		uris.length ? focusMarkers() : focusDefaultView();
	}, [inited]);

	const guarded = useSelector(guardedIssuesSelector);
	const hot = useSelector(hotIssuesSelector);
	const mru = useSelector(mruSelector);
	const resolutionTypes = useSelector(resolutionTypesSelector);
	const categories = useSelector(categoriesSelector);
	const issueTypes = useSelector(issuesTypesStateSelector);
	const issueTypesMap = new Map();

	if (issueTypes?.list?.length > 0) {
		for (const issueType of issueTypes?.list) {
			issueTypesMap.set(issueType.id, issueType.mnemonics);
		}
	}

	const airsideMonitoringIds = categories?.list
		?.filter((category) => category.mnemonics === 'airside-monitored')
		?.map((category) => category.categoryId);

	const digest = useSelector(digestSelector);
	const runways = useSelector(runwaysSelector);
	useEffect(() => {
		focusMarkers();
	}, [props.match.params.uris]);

	useEffect(() => {
		if (props.zones.map != null) {
			const zones = Object.values(props.zones.map);
			const layers = {};
			zones.forEach((zone) => {
				const layer = new VectorLayer({
					source: new VectorSource({
						features: setProps(readMultiPolygon(zone.geometry), {
							zoneId: zone.zoneId,
							name: zone.name,
							style: zone.style,
						}),
					}),
					style: makeStyle(StyleType.Default),
				});
				layer.setProperties({ zoneId: zone.zoneId });
				layers[zone.zoneId] = layer;
			});
			layerGroup.current = new LayerGroup({ layers: Object.values(layers) });
			map.current.getOlMap().addLayer(layerGroup.current);
			layerGroup.current.setVisible(displayZones);
		}
	}, [props.zones]);

	useEffect(() => {
		if (layerGroup.current != null) {
			layerGroup.current.setVisible(displayZones);
		}
	}, [displayZones, guarded, digest]);

	const focusExtent = (extent) => {
		map.current.getOwMap().fitExtent(extent);
	};
	const dispatch = useDispatch();
	useEffect(() => {
		if (runways.list) {
			runways.list.forEach((runway) => {
				dispatch(actions.load.request({ runwayId: runway.id }));
			});
		}
	}, [runways]);

	useEffect(() => {
		dispatch(actions.subscribe());
		dispatch(runwayActions.load.request());
		dispatch(runwayActions.statuses.request());

		return () => {
			dispatch(actions.unsubscribe());
		};
	}, []);

	const getDeviceIsAlerting = useCallback(
		(device) => {
			return !!mru.hasOwnProperty([device.uri]);
		},
		[mru],
	);

	const getBadgeType = (device, kind) => {
		let isInOpenRunway = false;
		if (digest.digests) {
			for (const key in digest.digests) {
				isInOpenRunway =
          digest.digests[key].guarded?.some((vehicle) => vehicle.uri === device.uri) && digest.digests[key].runwayOpen;
				if (isInOpenRunway) break;
			}
		}

		const isAirsideMonitoring = device.categoryIds?.filter((id) => airsideMonitoringIds?.includes(id)).length;

		if (isAirsideMonitoring) {
			const issueTypeMnemonic = issueTypesMap.get(guarded[device.uri]?.issueTypeId);
			const isRWYVehicle =
        issueTypeMnemonic === DeviceIssueMnemonics.VehicleInRunway ||
        issueTypeMnemonic === DeviceIssueMnemonics.UnapprovedVehicleInRunway ||
        issueTypeMnemonic === DeviceIssueMnemonics.UnauthorizedVehicleInRunway;

			if (guarded[device.uri]?.severity === 'NOTICE') {
				if (isRWYVehicle) {
					return BadgeTypes.WarningGreenRWY;
				}

				return BadgeTypes.WarningGreen;
			}
			if (guarded[device.uri]?.severity === 'WARNING') {
				if (kind === ResolutionKind.AuthorizedSituation || kind === ResolutionKind.FalsePositive) {
					if (isRWYVehicle) {
						return BadgeTypes.AlertGreenRWY;
					}
					return BadgeTypes.AlertGreen;
				}
				if (isRWYVehicle) {
					return BadgeTypes.AlertOrangeRWY;
				}
				return BadgeTypes.AlertOrange;
			}
			if (guarded[device.uri]?.severity === 'SEVERE' || guarded[device.uri]?.severity === 'ALERT') {
				if (kind === ResolutionKind.AuthorizedSituation || kind === ResolutionKind.FalsePositive) {
					if (isRWYVehicle) {
						return BadgeTypes.SevereGreenRWY;
					}
					return BadgeTypes.SevereGreen;
				}
				if (isRWYVehicle) {
					return BadgeTypes.SevereRedRWY;
				}
				return BadgeTypes.SevereRed;
			}
		}

		if (!guarded[device.uri]) {
			return BadgeTypes.Unknown;
		}

		return null;
	};

	const markers = useMemo(() => {
		if (!props.devices.list || !currentCategoriesIds || !hiddenCategoriesIds) {
			return [];
		}

		const resolutionTypeMap = resolutionTypes?.map;

		return props.devices.list.reduce((devices, device) => {
			const resolutionKind = resolutionTypeMap?.[guarded[device.uri]?.resolutionTypeId]?.resolutionKind;
			const badgeType = getBadgeType(device, resolutionKind);

			const params = {
				autoPan: uris.includes(device.uri),
				autoPanMargin: 100,
				noInitialFocus: true,
				badgeType,
				isAlerting: getDeviceIsAlerting(device),
				isHidable: true,
			};

			const isCategorizedHidden =
        device.categoryIds && hiddenCategoriesIds.some((id) => device.categoryIds.includes(id));

			const isNotCategorizedHidden =
        hiddenCategoriesIds.includes(0) &&
        (!device.categoryIds || !currentCategoriesIds.some((id) => device.categoryIds.includes(id)));

			return isCategorizedHidden || isNotCategorizedHidden
				? devices
				: [...devices, <DeviceStateMapMarker key={device.uri} params={params} device={device} />];
		}, []);
	}, [guarded, hot, mru, digest, uris, props.devices.list, resolutionTypes, currentCategoriesIds, hiddenCategoriesIds]);

	const MapControl = props.control;

	return (
		<MarkerOrderManager orderOption={orderOption}>
			<MarkerHoverManager map={map.current}>
				<OwMap className={className('overview', mapName)} name={mapName} ref={map} baseLayer={OwMap.Layers.ONE}>
					{markers}
				</OwMap>
				{MapControl && (
					<MapControl
						{...layersMethods}
						mapName={mapName}
						focusExtent={focusExtent}
						displayZones={displayZones}
						setDisplayZones={setDisplayZones}
						saveMapView={saveMapView}
						dropdownOptions={<MarkerOrderOption value={orderOption} onChange={setOrderOption} />}
					/>
				)}
				<Compass mapName={mapName} />
			</MarkerHoverManager>
		</MarkerOrderManager>
	);
}

export default connect((state) => {
	return {
		devices: state.devices,
		zones: state.zones,
		categoriesVisibility: state.categories.categoriesVisibility,
	};
})(withRouter(OverviewMap));
